import { Outlet, useLocation } from 'react-router-dom';

import { LiveNotifier } from '@/components/notifications/LiveNotifier';
import { RightRailWidgets } from '@/pages/timeline/components/RightRailWidgets';

import { Topbar } from './Topbar';
import { DashboardSidebar } from './DashboardSidebar';
import { RightRail } from './RightRail';
import { MobileBottomNav } from './MobileBottomNav';
import { ImpersonationBanner } from './ImpersonationBanner';

/**
 * Three-column dashboard shell: top bar, left sidebar, main content, right rail,
 * plus the mobile bottom nav. Content gets extra bottom padding on mobile so it
 * clears the fixed bottom nav.
 *
 * Right-rail policy (see `rightRailMode`):
 *   - `messages` is a full-width 3-pane messenger, so the rail is suppressed.
 *   - Form / list pages that already carry their own content (Notifications,
 *     Friends, Visitors, Invite Friends, Places, MyBlogs, Edit Profile,
 *     Settings) hide the global rail to reduce noise; pages that need their
 *     own widgets render them inline (e.g. Friends has a page-level
 *     `FriendsRightRail`).
 *   - The People search (`/users`) and the friend-requests inbox
 *     (`/dashboard/friend-requests`) keep `Suggested for you` but drop
 *     `Trending` (hashtags are off-topic in a people directory / requests UI).
 *   - Everything else renders the global Trending + Suggested.
 */
type RailMode = 'full' | 'suggested-only' | 'hidden';

function rightRailMode(pathname: string): RailMode {
  if (pathname.startsWith('/dashboard/messages')) return 'hidden';

  const hidden =
    pathname === '/dashboard/notifications' ||
    pathname === '/dashboard/blogs' ||
    pathname === '/dashboard/friends' ||
    pathname === '/dashboard/visitors' ||
    pathname === '/dashboard/invite-friends' ||
    pathname === '/dashboard/livestream' ||
    pathname === '/places' ||
    pathname.startsWith('/profile/edit') ||
    pathname.startsWith('/dashboard/settings');
  if (hidden) return 'hidden';

  if (
    pathname === '/users' ||
    pathname.startsWith('/users/') ||
    pathname === '/dashboard/friend-requests'
  ) {
    return 'suggested-only';
  }
  return 'full';
}

export function DashboardLayout() {
  const { pathname } = useLocation();
  const rail = rightRailMode(pathname);

  return (
    <div className="min-h-screen bg-canvas">
      {/* Yellow banner sits ABOVE every layout chrome (Topbar, sidebar,
          content). Renders nothing when impersonation.active === false. */}
      <ImpersonationBanner />
      <Topbar />
      <div className="mx-auto flex max-w-7xl items-start gap-3 px-4 max-[420px]:px-3 sm:px-6 min-[1100px]:gap-6">
        <DashboardSidebar />
        {/* `min-[991px]:pt-6` (NOT `py-6`) so the top padding bumps at 991+
            without nuking the `pb-20` bottom clearance for the mobile bottom
            nav (which still shows below `lg`). `lg:pb-6` then trims the
            bottom back once the nav is hidden at desktop. */}
        <main className="min-w-0 flex-1 py-4 pb-20 min-[991px]:pt-6 lg:pb-6">
          <Outlet />
        </main>
        {rail === 'hidden' ? null : (
          <RightRail>
            <RightRailWidgets
              // Trending hidden across all dashboard surfaces for now —
              // flip back to `rail === 'full'` to re-enable.
              showTrending={false}
              showSuggested={true}
              showLastLogged={rail === 'full'}
              showNearby={false}
              // Bottom-of-rail AdSense banner removed (2026-06-25).
              // Flip back to `rail === 'full'` to re-enable on /dashboard
              // and /hashtag/:tag.
              showAd={false}
            />
          </RightRail>
        )}
      </div>
      <MobileBottomNav />

      {/* Background notifier — fires a toast in the top-right when someone
          goes live since the last poll. Renders nothing visible. Only
          active for authenticated viewers. */}
      <LiveNotifier />
    </div>
  );
}
